/**
 * Combat steps data for the MTG Rules Reference.
 * Defines the 5 combat steps with key rules and example scenarios.
 *
 * CommonJS module for compatibility with both backend (require) and
 * frontend (import via Webpack/Babel interop).
 *
 * @module rules/combatData
 */

const COMBAT_STEP_IDS = {
  BEGINNING: 'beginning-of-combat',
  DECLARE_ATTACKERS: 'declare-attackers',
  DECLARE_BLOCKERS: 'declare-blockers',
  COMBAT_DAMAGE: 'combat-damage',
  END_OF_COMBAT: 'end-of-combat',
};

const COMBAT_STEPS = [
  {
    id: COMBAT_STEP_IDS.BEGINNING,
    name: 'Beginning of Combat',
    description:
      'The first step of the combat phase. No creatures are attacking or blocking yet. "At the beginning of combat" triggered abilities trigger. The active player receives priority. This is the last chance to tap down potential attackers or activate abilities before attackers are declared.',
    keyRules: [
      '"At the beginning of combat on your turn" abilities trigger here.',
      'Both players receive priority before attackers are declared.',
      'The defending player can tap potential attackers with effects like Icy Manipulator to prevent them from attacking.',
      'The attacking player can activate abilities or cast instants before committing to attacks.',
    ],
    examples: [
      {
        scenario: 'A player controls Goblin Rabblemaster, which triggers at the beginning of combat to create a 1/1 Goblin token.',
        resolution: 'The token is created before attackers are declared, so it can attack this turn (Rabblemaster forces it to attack if able).',
      },
      {
        scenario: 'An opponent controls a Thalia, Heretic Cathar. The defending player uses this step to cast removal on Thalia before declaring attackers.',
        resolution: 'With Thalia removed, their creatures can now enter untapped normally.',
      },
    ],
  },
  {
    id: COMBAT_STEP_IDS.DECLARE_ATTACKERS,
    name: 'Declare Attackers',
    description:
      'The active player declares which creatures are attacking and which players, planeswalkers, or battles they are attacking. Attacking creatures are tapped simultaneously (unless they have vigilance). Once attackers are declared, "whenever this creature attacks" triggered abilities trigger. Then the active player receives priority.',
    keyRules: [
      'Only untapped creatures that have been under your control since the start of your turn (or have haste) can attack.',
      'Attacking causes a creature to tap unless it has vigilance.',
      'Each creature can only attack one player, planeswalker, or battle.',
      '"Whenever this creature attacks" and "whenever you attack" abilities trigger after attackers are declared.',
      'Costs to attack (e.g., "attacks only if you pay {2}") must be paid as attackers are declared.',
    ],
    examples: [
      {
        scenario: 'A player declares their 4/4 Questing Beast and 2/2 Robber of the Rich as attackers. Both tap.',
        resolution: 'Robber of the Rich\'s "whenever Robber of the Rich attacks" ability triggers and is placed on the stack.',
      },
      {
        scenario: 'A player controls a creature enchanted with Brave the Sands, giving it vigilance.',
        resolution: 'That creature can attack without tapping, remaining available to block on the opponent\'s turn.',
      },
    ],
  },
  {
    id: COMBAT_STEP_IDS.DECLARE_BLOCKERS,
    name: 'Declare Blockers',
    description:
      'The defending player declares which of their untapped creatures will block which attacking creatures. Multiple creatures can block a single attacker. If an attacker is blocked by multiple creatures, the attacking player orders the blockers for damage assignment. After blockers are declared, "whenever this creature blocks" triggered abilities trigger. Then the active player receives priority.',
    keyRules: [
      'Each blocking creature can only block one attacker (unless it has an ability that lets it block additional creatures).',
      'Multiple creatures can block the same attacker.',
      'The attacking player orders blockers on each multiply-blocked attacker for damage assignment.',
      'A creature that has been blocked remains blocked even if the blocker is removed before combat damage, unless the attacker has trample.',
      'Flying creatures can only be blocked by creatures with flying or reach.',
      'Menace creatures can only be blocked by two or more creatures.',
    ],
    examples: [
      {
        scenario: 'An opponent attacks with a 5/5 Baneslayer Angel (flying). The defending player tries to block with a 2/4 Archon of Absolution (flying) and a 1/3 Wall of Runes (no flying or reach).',
        resolution: 'The block with Wall of Runes is illegal. Only creatures with flying or reach can block a flyer. Only Archon of Absolution can legally block.',
      },
      {
        scenario: 'An opponent attacks with a 3/3 creature. The defending player blocks with two 2/2 creatures.',
        resolution: 'The attacker\'s controller orders the blockers: Blocker A first, Blocker B second. During damage, the attacker must assign lethal damage (2) to Blocker A before assigning remaining damage (1) to Blocker B.',
      },
    ],
  },
  {
    id: COMBAT_STEP_IDS.COMBAT_DAMAGE,
    name: 'Combat Damage',
    description:
      'Each attacking and blocking creature assigns combat damage equal to its power. Damage is assigned simultaneously and dealt simultaneously (unless first strike or double strike creates an additional combat damage step). Creatures deal damage to what they are in combat with: unblocked attackers deal damage to the player, planeswalker, or battle they attacked. Blocked attackers deal damage to their blockers. Blockers deal damage to the creature they blocked.',
    keyRules: [
      'Combat damage is dealt simultaneously by all creatures without first strike or double strike.',
      'If any creature has first strike or double strike, there is an additional first-strike combat damage step before the regular one.',
      'An attacker blocked by multiple creatures must assign lethal damage to the first blocker in order before assigning damage to the next.',
      'Trample allows excess damage to be assigned to the defending player, planeswalker, or battle after assigning lethal damage to all blockers.',
      'Combat damage cannot be redirected. Creatures deal damage to what they are in combat with.',
      'Players cannot cast spells or activate abilities between damage assignment and damage being dealt.',
    ],
    examples: [
      {
        scenario: 'A 3/3 creature with first strike is blocked by a 2/2 creature.',
        resolution: 'In the first-strike damage step, the 3/3 deals 3 damage to the 2/2, destroying it. The 2/2 never gets to deal its regular combat damage because it is already destroyed.',
      },
      {
        scenario: 'A 6/6 creature with trample is blocked by a 2/3.',
        resolution: 'The attacker assigns 3 damage (lethal) to the blocker and the remaining 3 damage tramples over to the defending player.',
      },
      {
        scenario: 'A 4/4 creature with double strike is blocked by a 5/5.',
        resolution: 'In the first-strike step, the 4/4 deals 4 damage to the 5/5. In the regular step, the 4/4 deals another 4 damage (total 8) and the 5/5 deals 5 damage back. Both are destroyed.',
      },
    ],
  },
  {
    id: COMBAT_STEP_IDS.END_OF_COMBAT,
    name: 'End of Combat',
    description:
      'The final step of the combat phase. "At end of combat" triggered abilities trigger. The active player receives priority. After this step, creatures are removed from combat and are no longer considered attacking or blocking.',
    keyRules: [
      '"At end of combat" and "until end of combat" effects are processed here.',
      'Creatures are still considered attacking/blocking during this step.',
      'After this step ends, all creatures are removed from combat.',
      'Effects that last "until end of combat" wear off as this step ends.',
      'This is the last chance to interact with creatures while they are still "attacking" or "blocking."',
    ],
    examples: [
      {
        scenario: 'A creature is enchanted with an Aura that says "Enchant attacking creature." The end of combat step finishes.',
        resolution: 'The creature is no longer attacking, so the Aura is illegally attached and falls off as a state-based action.',
      },
      {
        scenario: 'Reconnaissance is activated during this step targeting an attacking creature that already dealt combat damage.',
        resolution: 'The creature is untapped and removed from combat. It effectively attacked, dealt damage, and is now untapped, since it is still technically "attacking" during this step.',
      },
    ],
  },
];

module.exports = {
  COMBAT_STEP_IDS,
  COMBAT_STEPS,
};
